// Configuration resolver for MSAL and auth-related settings
//
// Purpose:
// - Consolidate logic to derive MSAL clientId, authority, and API scopes.
// - Fetch backend public config (/api/publicconfig) and fall back to env vars.
// - Cache the resolved config so network calls are only performed once.

const baseScopes = ['openid', 'profile', 'email'];

export interface AuthConfig {
  clientId: string;
  authority: string;
  apiScope: string;
  loginScopes: string[];
  graphScopes: string[];
}

let cachedConfig: AuthConfig | null = null;
let resolving: Promise<AuthConfig> | null = null;

export async function resolveConfig(): Promise<AuthConfig> {
	// Return cached result if already resolved
	if (cachedConfig) return cachedConfig;
	// If a resolution is already in flight, return the same promise so callers share the network request
	if (resolving) return resolving;

	resolving = (async () => {
		// Read from env vars is not used, I rely on the publicconfig api
		let apiScope = (import.meta.env.VITE_AZURE_AD_API_SCOPE || '').trim();
		let clientId = (import.meta.env.VITE_AZURE_AD_CLIENT_ID || '').trim();
		let authority = (import.meta.env.VITE_AZURE_AD_AUTHORITY || '').trim();

		try {
			const res = await fetch('/api/publicconfig');
			if (res.ok) {
				const json = await res.json();
				if (json.clientId) clientId = String(json.clientId);
				if (json.authority) authority = String(json.authority);
			}
		} catch (e) {
			// Ignore and fall back to env vars
			console.warn('Failed to fetch backend config, falling back to env vars', e);
		}

		if ((!apiScope || apiScope.length === 0) && clientId) {
			apiScope = `api://${clientId}/Api.Access`;
		}

		//graph scopes for MS Graph API access voor het syncen van user groups in de api
		const graphScopes = ['User.Read', 'GroupMember.Read.All'];
		const loginScopes = apiScope ? [...baseScopes, apiScope] : baseScopes;

		cachedConfig = { clientId, authority, apiScope, loginScopes, graphScopes };
		resolving = null;
		return cachedConfig;
	})();

	return resolving;
}

// Expose the cached config so other modules can access it without owning cache logic.
export function getCachedConfig(): AuthConfig | null {
	return cachedConfig;
}